import { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'

// Retired hardware is hidden here rather than filtered in the query, so the app
// works whether or not the `active` column exists yet — a row from before the
// migration has no `active` at all, and counts as live.
const isLive = (row) => row.active !== false

// Branches now carry tanks[] AND branch-level pumps[] (pumps are per branch+product).
function buildBranchTree(branchRows, tankRows, pumpRows) {
  const tanksByBranchId = {}
  tankRows.filter(isLive).forEach((t) => {
    if (!tanksByBranchId[t.branch_id]) tanksByBranchId[t.branch_id] = []
    tanksByBranchId[t.branch_id].push({
      id: t.id,
      product: t.product,
      tankNumber: t.tank_number,
      capacity: Number(t.capacity),
      valveOpen: t.valve_open !== false,
    })
  })

  const pumpsByBranchId = {}
  pumpRows.filter(isLive).forEach((p) => {
    if (!pumpsByBranchId[p.branch_id]) pumpsByBranchId[p.branch_id] = []
    pumpsByBranchId[p.branch_id].push({
      id: p.id,
      product: p.product,
      pumpNumber: p.pump_number,
      name: p.name || '',
      defaultExcessPer20L: p.default_excess_per_20l != null ? Number(p.default_excess_per_20l) : 1.0,
    })
  })

  return branchRows.map((b) => ({
    id: b.id,
    name: b.name,
    location: b.location,
    organizationId: b.organization_id,
    tanks: tanksByBranchId[b.id] || [],
    pumps: pumpsByBranchId[b.id] || [],
  }))
}

// Insert a branch's tanks + branch-level pumps (pumps numbered per product).
async function insertTanksAndPumps(branchId, tanks = [], pumps = []) {
  for (const t of tanks) {
    await supabase.from('tanks').insert({
      branch_id: branchId, product: t.product, tank_number: Number(t.tankNumber), capacity: Number(t.capacity),
    })
  }
  const perProduct = {}
  for (const p of pumps) {
    perProduct[p.product] = (perProduct[p.product] || 0) + 1
    await supabase.from('pumps').insert({
      branch_id: branchId, product: p.product,
      pump_number: p.pumpNumber || perProduct[p.product],
      name: p.name || '', default_excess_per_20l: p.defaultExcessPer20L ?? 1.0,
    })
  }
}

export function useOrgData() {
  const [organizations, setOrganizations] = useState([])
  const [branches, setBranches] = useState([])

  useEffect(() => { fetchAll() }, [])

  async function fetchAll() {
    const [orgsRes, branchesRes, tanksRes, pumpsRes] = await Promise.all([
      supabase.from('organizations').select('*').order('created_at'),
      supabase.from('branches').select('*').order('created_at'),
      supabase.from('tanks').select('*').order('tank_number'),
      supabase.from('pumps').select('*').order('pump_number'),
    ])
    setOrganizations((orgsRes.data || []).map((o) => ({
      id: o.id, name: o.name, location: o.location, description: o.description,
      plan: o.plan || 'starter',
      subscriptionStatus: o.subscription_status || 'trialing',
      suspendedAt: o.suspended_at ? new Date(o.suspended_at) : null,
      suspendedReason: o.suspended_reason || '',
      graceUntil: o.grace_until ? new Date(o.grace_until) : null,
      trialEndsAt: o.trial_ends_at ? new Date(o.trial_ends_at) : null,
      currentPeriodEnd: o.current_period_end ? new Date(o.current_period_end) : null,
      createdAt: o.created_at ? new Date(o.created_at) : null,
      // Signup-form answers (self-service orgs; null on manually created ones)
      businessType: o.business_type || null,
      cacNumber: o.cac_number || null,
      companySize: o.company_size || null,
      numBranches: o.num_branches ?? null,
    })))
    setBranches(buildBranchTree(branchesRes.data || [], tanksRes.data || [], pumpsRes.data || []))
  }

  // Duplicate branch names per org are blocked by a case-insensitive unique
  // index (branch_name_uniqueness_migration.sql) — turn its 23505 into a
  // message a human can act on.
  const friendlyBranchError = (error, verb) => ({
    success: false,
    message: error.code === '23505'
      ? `A branch with this name already exists in this organization (names are case-insensitive).`
      : `Failed to ${verb} branch: ${error.message}`,
  })

  // --- Organization CRUD ---
  async function addOrganization(orgData, newBranches = []) {
    const { data: org, error } = await supabase
      .from('organizations')
      .insert({ name: orgData.name.trim(), location: (orgData.location || '').trim(), description: orgData.description || '' })
      .select().single()
    if (error) return { success: false, message: 'Failed to create organization: ' + error.message }
    for (const b of newBranches) {
      if (!b.name?.trim()) continue
      const { data: branch, error: bErr } = await supabase.from('branches').insert({ organization_id: org.id, name: b.name.trim(), location: (b.location || '').trim() }).select().single()
      if (bErr) { await fetchAll(); return friendlyBranchError(bErr, 'create') }
      await insertTanksAndPumps(branch.id, b.tanks, b.pumps)
    }
    await fetchAll()
    return { success: true }
  }

  async function updateOrganization(orgId, orgData, branchUpdates = null) {
    await supabase.from('organizations').update({ name: orgData.name.trim(), location: (orgData.location || '').trim(), description: orgData.description || '' }).eq('id', orgId)
    if (branchUpdates !== null) {
      const existingBranches = branches.filter((b) => b.organizationId === orgId)
      const incomingIds = new Set(branchUpdates.filter((b) => b.id).map((b) => b.id))
      for (const eb of existingBranches) if (!incomingIds.has(eb.id)) await supabase.from('branches').delete().eq('id', eb.id)
      for (const b of branchUpdates) {
        const row = { name: b.name.trim(), location: (b.location || '').trim() }
        const { error: bErr } = b.id
          ? await supabase.from('branches').update(row).eq('id', b.id)
          : await supabase.from('branches').insert({ organization_id: orgId, ...row })
        if (bErr) { await fetchAll(); return friendlyBranchError(bErr, 'save') }
      }
    }
    await fetchAll()
    return { success: true }
  }

  // Superadmin: change an org's plan tier and/or grant trial days (docs/15).
  // - plan change writes organizations.plan only (the branch-limit trigger keys
  //   off it; Paystack's charge.success never rewrites plan, so a manual tier
  //   survives recurring payments).
  // - trialDays > 0 sets trial_ends_at = now + N days on the org AND every
  //   profile in it (the lock gate reads the PROFILE copy — same dual-write the
  //   mobile useExtendTrial does), plus subscription_status='trialing' so a
  //   lapsed/cancelled org actually unlocks.
  async function applyPlanAndTrial(orgId, { plan, trialDays, comped = null }) {
    const orgUpdate = {}
    if (plan) orgUpdate.plan = plan
    let newTrialEnd = null
    if (trialDays > 0) {
      const d = new Date()
      d.setDate(d.getDate() + trialDays)
      newTrialEnd = d.toISOString()
      orgUpdate.trial_ends_at = newTrialEnd
      orgUpdate.subscription_status = 'trialing'
    }
    // Comp/un-comp wins over the trial-driven status: comped = free forever,
    // never locked; un-comping drops back to 'trialing' (trial_ends_at rules).
    if (comped === true) orgUpdate.subscription_status = 'comped'
    else if (comped === false && !newTrialEnd) orgUpdate.subscription_status = 'trialing'
    if (Object.keys(orgUpdate).length === 0) return { success: true }

    const { error } = await supabase.from('organizations').update(orgUpdate).eq('id', orgId)
    if (error) return { success: false, message: 'Failed to update plan: ' + error.message }

    if (newTrialEnd) {
      const { error: pErr } = await supabase.from('profiles')
        .update({ trial_ends_at: newTrialEnd }).eq('organization_id', orgId)
      if (pErr) return { success: false, message: 'Plan saved, but extending member trials failed: ' + pErr.message }
    }
    await fetchAll()
    return { success: true }
  }

  // Manual block, independent of payment state. Kept in its own column rather
  // than written as subscription_status='cancelled' so the next Paystack
  // webhook can't silently undo it — and so restoring doesn't have to guess
  // what the billing status was beforehand.
  async function suspendOrganization(orgId, reason, actorId) {
    const { error } = await supabase.from('organizations').update({
      suspended_at: new Date().toISOString(),
      suspended_reason: (reason || '').trim(),
      suspended_by: actorId || null,
    }).eq('id', orgId)
    if (error) return { success: false, message: 'Failed to suspend: ' + error.message }
    await fetchAll()
    return { success: true }
  }

  async function restoreOrganization(orgId) {
    const { error } = await supabase.from('organizations').update({
      suspended_at: null, suspended_reason: null, suspended_by: null,
    }).eq('id', orgId)
    if (error) return { success: false, message: 'Failed to restore: ' + error.message }
    await fetchAll()
    return { success: true }
  }

  async function deleteOrganization(orgId) {
    await supabase.from('organizations').delete().eq('id', orgId)
    await fetchAll()
    return { success: true }
  }

  // --- Branch CRUD ---
  async function addBranch(branchData) {
    const { data: branch, error } = await supabase.from('branches').insert({ organization_id: branchData.organizationId, name: branchData.name.trim(), location: (branchData.location || '').trim() }).select().single()
    if (error) return friendlyBranchError(error, 'create')
    await insertTanksAndPumps(branch.id, branchData.tanks, branchData.pumps)
    await fetchAll()
    return { success: true }
  }

  // Branch details only. This used to delete and re-insert the branch's tanks
  // and pumps on every save, which cascaded away every dip reading, meter
  // reading and meter photo attached to them. Tanks and pumps are now edited
  // one at a time from the Station Calibration console.
  async function updateBranch(branchId, branchData) {
    const { error } = await supabase.from('branches').update({ organization_id: branchData.organizationId, name: branchData.name.trim(), location: (branchData.location || '').trim() }).eq('id', branchId)
    if (error) return friendlyBranchError(error, 'save')
    await fetchAll()
    return { success: true }
  }

  async function deleteBranch(branchId) {
    await supabase.from('branches').delete().eq('id', branchId)
    await fetchAll()
    return { success: true }
  }

  // --- Product lines (Station Calibration console) ---
  //
  // A product line is every tank storing a product at a branch plus every pump
  // selling it. The pumps share one manifold, so no pump belongs to a tank —
  // which tank is being drawn from is decided by the valves below.

  // Open or close one tank's valve. Several tanks on a line may sell at once,
  // but the line always needs one open — closing the last one would leave the
  // pumps drawing from nothing.
  async function setValve(tankId, open) {
    const branch = branches.find((b) => b.tanks.some((t) => t.id === tankId))
    const tank = branch?.tanks.find((t) => t.id === tankId)
    if (!tank) return { success: false, message: 'Tank not found.' }

    if (!open) {
      const openOnLine = branch.tanks.filter((t) => t.product === tank.product && t.valveOpen)
      if (openOnLine.length <= 1) {
        return { success: false, message: `Tank ${tank.tankNumber} is the only open ${tank.product} tank — open another before closing this one.` }
      }
    }

    const { error } = await supabase.from('tanks').update({ valve_open: open }).eq('id', tankId)
    if (error) return { success: false, message: 'Failed to change the valve: ' + error.message }
    await fetchAll()
    return { success: true }
  }

  // Calibration is whatever the station says it is — any value >= 0.
  async function setPumpCalibration(pumpId, excessPer20L) {
    const value = Number(excessPer20L)
    if (!Number.isFinite(value) || value < 0) return { success: false, message: 'Calibration must be 0 or more.' }
    const { error } = await supabase.from('pumps').update({ default_excess_per_20l: value }).eq('id', pumpId)
    if (error) return { success: false, message: 'Failed to save calibration: ' + error.message }
    await fetchAll()
    return { success: true }
  }

  // Add a whole line at once: the product, its tanks, and the pumps that sell it.
  async function addProductLine(branchId, { product, tankCount, capacity, pumpCount }) {
    const branch = branches.find((b) => b.id === branchId)
    if (!branch) return { success: false, message: 'Station not found.' }
    if (branch.tanks.some((t) => t.product === product) || branch.pumps.some((p) => p.product === product)) {
      return { success: false, message: `${product} already has a line at "${branch.name}" — edit that line instead.` }
    }

    // Both run station-wide and skip numbers already used by retired rows.
    const nextTankNumber = await nextNumber('tanks', branchId, 'tank_number')
    const nextPumpNumber = await nextNumber('pumps', branchId, 'pump_number')
    // The first tank opens so the line can sell immediately; the rest are reserve.
    const tanks = Array.from({ length: tankCount }, (_, i) => ({
      branch_id: branchId, product, tank_number: nextTankNumber + i, capacity: Number(capacity),
      valve_open: i === 0,
    }))
    const pumps = Array.from({ length: pumpCount }, (_, i) => ({
      branch_id: branchId, product, pump_number: nextPumpNumber + i,
      name: `Pump ${nextPumpNumber + i}`, default_excess_per_20l: 1.0,
    }))

    const { error: tErr } = await supabase.from('tanks').insert(tanks)
    if (tErr) return { success: false, message: 'Failed to add tanks: ' + tErr.message }
    const { error: pErr } = await supabase.from('pumps').insert(pumps)
    if (pErr) return { success: false, message: 'Tanks added, but pumps failed: ' + pErr.message }

    await fetchAll()
    return { success: true }
  }

  // Tank and pump numbers run station-wide, and are never reused — a retired
  // Tank 3 keeps its readings, so a new Tank 3 would make that history
  // ambiguous. Retired rows aren't in local state, so ask the server.
  async function nextNumber(table, branchId, column) {
    const { data } = await supabase
      .from(table).select(column).eq('branch_id', branchId)
      .order(column, { ascending: false }).limit(1)
    return (data?.[0]?.[column] ?? 0) + 1
  }

  const findLine = (branchId, product) => {
    const branch = branches.find((b) => b.id === branchId)
    if (!branch) return null
    return {
      branch,
      tanks: branch.tanks.filter((t) => t.product === product),
      pumps: branch.pumps.filter((p) => p.product === product),
    }
  }

  async function addTank(branchId, { product, capacity }) {
    const value = Number(capacity)
    if (!Number.isFinite(value) || value <= 0) return { success: false, message: 'Capacity must be more than 0.' }
    const tankNumber = await nextNumber('tanks', branchId, 'tank_number')
    // New tanks arrive closed: a tank nobody has dipped yet should not start
    // feeding the pumps the moment it is created.
    const { error } = await supabase.from('tanks').insert({
      branch_id: branchId, product, tank_number: tankNumber, capacity: value, valve_open: false,
    })
    if (error) return { success: false, message: 'Failed to add the tank: ' + error.message }
    await fetchAll()
    return { success: true, tankNumber }
  }

  async function updateTank(tankId, { capacity }) {
    const value = Number(capacity)
    if (!Number.isFinite(value) || value <= 0) return { success: false, message: 'Capacity must be more than 0.' }
    const { error } = await supabase.from('tanks').update({ capacity: value }).eq('id', tankId)
    if (error) return { success: false, message: 'Failed to save the tank: ' + error.message }
    await fetchAll()
    return { success: true }
  }

  async function retireTank(tankId) {
    const branch = branches.find((b) => b.tanks.some((t) => t.id === tankId))
    const tank = branch?.tanks.find((t) => t.id === tankId)
    if (!tank) return { success: false, message: 'Tank not found.' }

    const onLine = branch.tanks.filter((t) => t.product === tank.product)
    if (onLine.length <= 1) {
      return { success: false, message: `Tank ${tank.tankNumber} is the only ${tank.product} tank — the pumps would have nothing to draw from. Add another tank first.` }
    }
    if (tank.valveOpen && onLine.filter((t) => t.valveOpen).length <= 1) {
      return { success: false, message: `Tank ${tank.tankNumber} is the only open ${tank.product} tank — open another before retiring this one.` }
    }

    const { error } = await supabase.from('tanks')
      .update({ active: false, retired_at: new Date().toISOString(), valve_open: false }).eq('id', tankId)
    if (error) return { success: false, message: 'Failed to retire the tank: ' + error.message }
    await fetchAll()
    return { success: true }
  }

  async function addPump(branchId, { product, name }) {
    const line = findLine(branchId, product)
    if (!line) return { success: false, message: 'Station not found.' }
    if (line.tanks.length === 0) {
      return { success: false, message: `Add a ${product} tank first — a pump needs something to draw from.` }
    }
    const pumpNumber = await nextNumber('pumps', branchId, 'pump_number')
    const { error } = await supabase.from('pumps').insert({
      branch_id: branchId, product, pump_number: pumpNumber,
      name: (name || '').trim() || `Pump ${pumpNumber}`, default_excess_per_20l: 1.0,
    })
    if (error) return { success: false, message: 'Failed to add the pump: ' + error.message }
    await fetchAll()
    return { success: true, pumpNumber }
  }

  async function updatePump(pumpId, { name }) {
    const { error } = await supabase.from('pumps').update({ name: (name || '').trim() }).eq('id', pumpId)
    if (error) return { success: false, message: 'Failed to save the pump: ' + error.message }
    await fetchAll()
    return { success: true }
  }

  async function retirePump(pumpId) {
    const branch = branches.find((b) => b.pumps.some((p) => p.id === pumpId))
    const pump = branch?.pumps.find((p) => p.id === pumpId)
    if (!pump) return { success: false, message: 'Pump not found.' }
    if (branch.pumps.filter((p) => p.product === pump.product).length <= 1) {
      return { success: false, message: `${pump.name || 'This pump'} is the only ${pump.product} pump — the line could not record a day's sales without it.` }
    }
    const { error } = await supabase.from('pumps')
      .update({ active: false, retired_at: new Date().toISOString() }).eq('id', pumpId)
    if (error) return { success: false, message: 'Failed to retire the pump: ' + error.message }
    await fetchAll()
    return { success: true }
  }

  // Retires the whole line. The tanks and pumps keep their readings — this is
  // the station saying "we no longer sell this product here".
  async function retireProductLine(branchId, product) {
    const retired = { active: false, retired_at: new Date().toISOString() }
    const { error: pErr } = await supabase.from('pumps').update(retired).eq('branch_id', branchId).eq('product', product)
    if (pErr) return { success: false, message: 'Failed to retire the pumps: ' + pErr.message }
    const { error: tErr } = await supabase.from('tanks').update({ ...retired, valve_open: false }).eq('branch_id', branchId).eq('product', product)
    if (tErr) return { success: false, message: 'Pumps retired, but the tanks failed: ' + tErr.message }
    await fetchAll()
    return { success: true }
  }

  return {
    organizations, branches, addOrganization, updateOrganization, deleteOrganization,
    applyPlanAndTrial, suspendOrganization, restoreOrganization,
    addBranch, updateBranch, deleteBranch,
    setValve, setPumpCalibration, addProductLine, retireProductLine,
    addTank, updateTank, retireTank, addPump, updatePump, retirePump,
  }
}
